import type { FC } from 'react';
import React, {
  Fragment,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {
  alpha,
  Box,
  ListItemAvatar,
  MenuItem,
  Stack,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Iconify,
  MenuPopover,
  NameAvatar,
  TruncatedTooltipText,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

export interface ScopeSecondaryPart {
  text: string;
  /** Renders italic: this entity contains the currently selected scope. */
  emphasized?: boolean;
}

export interface ScopeOption {
  id: string;
  primary: string;
  secondary?: ScopeSecondaryPart[];
}

export type ScopeAvatarColor = 'primary' | 'info' | 'warning';

export interface ScopeTab {
  key: string;
  label: string;
  avatarColor: ScopeAvatarColor;
  options: ScopeOption[];
  selectedId?: string;
  searchPlaceholder: string;
}

export interface SelectScopeViewProps {
  tabs: ScopeTab[];
  activeTabKey: string;
  buttonLabel: string;
  buttonIcon: string;
  onSelect: (tabKey: string, option: ScopeOption) => void;
}

const ITEM_HEIGHT = 60;
const SEPARATOR = ' · ';
/** Leaves room for the avatar and the always-reserved selection slot. */
const TEXT_MAX_WIDTH = 220;

const secondaryText = (parts: ScopeSecondaryPart[]): string =>
  parts.map((part) => part.text).join(SEPARATOR);

export const SelectScopeView: FC<SelectScopeViewProps> = ({
  tabs,
  activeTabKey,
  buttonLabel,
  buttonIcon,
  onSelect,
}) => {
  const { t } = useTranslation();
  const currentSelectionLabel = String(
    t('nav.scope-selection.current-selection')
  );
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [openTabKey, setOpenTabKey] = useState(activeTabKey);
  const menuItemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const openTab =
    tabs.find((tab) => tab.key === openTabKey) ?? tabs[0] ?? undefined;

  const avatarColor: ScopeAvatarColor = openTab?.avatarColor ?? 'primary';

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setOpen(event.currentTarget);
      setSearchQuery('');
      setFocusedIndex(-1);
      setOpenTabKey(activeTabKey);
    },
    [activeTabKey]
  );

  const handleClose = useCallback(() => {
    setOpen(null);
    setFocusedIndex(-1);
  }, []);

  const handleSelect = useCallback(
    (option: ScopeOption) => {
      if (!open || !openTab) {
        return;
      }

      onSelect(openTab.key, option);
      handleClose();
    },
    [onSelect, handleClose, open, openTab]
  );

  const handleTabChange = useCallback((_: unknown, nextKey: string) => {
    setOpenTabKey(nextKey);
    setSearchQuery('');
    setFocusedIndex(-1);

    // Keeps arrow keys walking the list instead of the tab strip.
    searchInputRef.current?.focus();
  }, []);

  const matchingOptions = useMemo(() => {
    const options = openTab?.options ?? [];
    if (!searchQuery.trim()) {
      return options;
    }

    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.primary.toLowerCase().includes(query) ||
        secondaryText(option.secondary ?? [])
          .toLowerCase()
          .includes(query)
    );
  }, [openTab, searchQuery]);

  const SCROLLBAR_HEIGHT = useMemo(() => {
    const maxVisibleItems = 5;
    const defaultHeight = maxVisibleItems * ITEM_HEIGHT;
    const nbrOfItems = matchingOptions.length;
    if (nbrOfItems === 0) {
      return ITEM_HEIGHT;
    }

    return nbrOfItems > maxVisibleItems
      ? defaultHeight
      : nbrOfItems * ITEM_HEIGHT;
  }, [matchingOptions]);

  // Reset focused index when the rendered list changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [matchingOptions]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && menuItemRefs.current[focusedIndex]) {
      menuItemRefs.current[focusedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [focusedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (matchingOptions.length === 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev + 1;
            return nextIndex >= matchingOptions.length ? 0 : nextIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev - 1;
            return nextIndex < 0 ? matchingOptions.length - 1 : nextIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < matchingOptions.length) {
            handleSelect(matchingOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          handleClose();
          break;
      }
    },
    [matchingOptions, focusedIndex, handleSelect, handleClose]
  );

  return (
    <>
      <SelectionButton
        direction="row"
        onClick={handleOpen}
        spacing={2}
        alignItems="center"
      >
        <Iconify
          icon={buttonIcon}
          width={20}
          height={20}
          sx={{ minWidth: 20 }}
        />
        <TruncatedTooltipText text={buttonLabel} noTooltip />
      </SelectionButton>
      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        arrow="top-left"
        sx={{
          mt: 0.5,
          ml: 6,
          width: 350,
          boxShadow: 'rgba(0, 0, 0, 0.15) 0px 5px 15px',
          pb: 2,
          paddingX: 1.2,
          '& .MuiMenuItem-root': {
            px: 1.5,
            height: ITEM_HEIGHT,
            borderRadius: 1.5,
          },
        }}
      >
        {/* The tabs are the heading, so there is no separate title. */}
        {tabs.length > 1 && (
          <Tabs
            value={openTab?.key ?? false}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 1 }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
        )}
        <Box sx={{ px: 1.5, py: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            inputRef={searchInputRef}
            placeholder={openTab?.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                startAdornment: (
                  <Iconify
                    icon="eva:search-fill"
                    sx={{
                      color: 'text.disabled',
                      width: 20,
                      height: 20,
                      mr: 1,
                    }}
                  />
                ),
              },
            }}
          />
        </Box>
        <Box style={{ overflowY: 'auto' }} sx={{ height: SCROLLBAR_HEIGHT }}>
          {matchingOptions.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                p: 2,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              {t('table.no-results')}
            </Typography>
          ) : (
            matchingOptions.map((option, index) => {
              const isSelected = openTab?.selectedId === option.id;
              const secondaryParts = option.secondary ?? [];
              return (
                <MenuItem
                  key={option.id}
                  ref={(el) => {
                    menuItemRefs.current[index] = el;
                  }}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  sx={{
                    bgcolor: isSelected
                      ? 'grey.200'
                      : focusedIndex === index
                      ? 'action.hover'
                      : '',
                  }}
                >
                  <ListItemAvatar sx={{ position: 'relative' }}>
                    <NameAvatar
                      name={option.primary}
                      sx={{
                        bgcolor: (theme) =>
                          alpha(theme.palette[avatarColor].main, 0.16),
                        color: (theme) => theme.palette[avatarColor].dark,
                      }}
                    />
                  </ListItemAvatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <TruncatedTooltipText
                      text={option.primary}
                      maxWidth={TEXT_MAX_WIDTH}
                      sx={{
                        typography: 'body2',
                        color: 'primary.main',
                      }}
                    />
                    {secondaryParts.length > 0 && (
                      // Not TruncatedTooltipText: that one only takes a plain
                      // string, and the containing entity renders italic.
                      <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={secondaryText(secondaryParts)}
                        sx={{ maxWidth: TEXT_MAX_WIDTH }}
                      >
                        {secondaryParts.map((part, partIndex) => (
                          <Fragment key={part.text}>
                            {partIndex > 0 && SEPARATOR}
                            <Box
                              component="span"
                              sx={
                                part.emphasized
                                  ? { fontStyle: 'italic' }
                                  : undefined
                              }
                            >
                              {part.text}
                            </Box>
                          </Fragment>
                        ))}
                      </Typography>
                    )}
                  </Box>
                  {/* Reserved so rows stay aligned whether selected or not. */}
                  <Box
                    role={isSelected ? 'img' : undefined}
                    aria-label={isSelected ? currentSelectionLabel : undefined}
                    sx={{
                      width: 24,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isSelected && (
                      <Iconify
                        icon="eva:checkmark-fill"
                        sx={{ width: 16, height: 16, color: 'primary.main' }}
                      />
                    )}
                  </Box>
                </MenuItem>
              );
            })
          )}
        </Box>
      </MenuPopover>
    </>
  );
};

const SelectionButton = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    mb: 5,
    cursor: 'pointer',
    bgcolor: 'secondary.light',
    pt: 1.6,
    pb: 1.4,
    pl: 2,
    pr: 2,
    borderRadius: 1.5,
  })
);
